/**
 * 앱 전역 "난수 미입력" 안내 배너용 상태 모듈.
 *
 * SPEC-CHINA-GLOBAL-RANDOMNO-BANNER-001
 *
 * 수기 작업지시서(china/dental-lab/work-orders) 전체를 훑어 난수가 비어 있는 건수를 집계한다.
 * 이 화면(/lab-orders/manual) 메뉴 권한이 없는 계정에는 절대 노출하지 않는다(fail-closed) —
 * 메뉴 권한 조회 실패도 미노출로 처리한다.
 *
 * @MX:NOTE: [AUTO] refresh는 ambient 안내용 백그라운드 집계라 실패 시 이전 값을 그대로 둔다.
 *   에러 UI를 노출하지 않는다(주 기능이 아닌 보조 알림이기 때문).
 */
import chinaInapi from '@/apis/chinaInapi';
import workOrderApi from '@/apis/workOrder';
import { userHasMenuLink } from '@/utils/landing';
import { hasMissingRandomNo } from '@/utils/missingRandomNo';

// 전량 조회 시 서버 왕복 상한 — 페이지당 100건 * 최대 20페이지 = 최대 2000건까지만 집계한다.
// 상한에 걸리면 truncated=true로 표시해 "N건 이상"임을 UI가 알 수 있게 한다.
// PAGE_LIMIT 100은 백엔드 상한이다 — 101 이상을 보내면 400 "limit must not be greater than 100"
// (2026-08-18 운영 china-inapi 실측). 절대 올리지 말 것.
const MAX_PAGES = 20;
const PAGE_LIMIT = 100;

export default {
  namespaced: true,

  state: () => ({
    count: 0,
    loading: false,
    loaded: false,
    eligible: false,
    // 10페이지 상한에 걸려 전량을 다 세지 못했는지 여부 — true면 배너가 "N건 이상"으로 표시한다.
    truncated: false,
  }),

  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setResult(state, { count, eligible, truncated }) {
      state.count = count;
      state.eligible = eligible;
      state.truncated = truncated;
      state.loaded = true;
    },
  },

  actions: {
    /**
     * 난수 미입력 건수를 재집계한다.
     *
     * - 로그인 토큰이 없으면 즉시 eligible:false로 확정한다.
     * - /lab-orders/manual 메뉴 권한이 없으면 eligible:false, count:0으로 확정한다(fail-closed).
     * - 조회 중 오류가 나면 이전 값을 그대로 두고 조용히 종료한다(ambient 안내이지 주 기능이 아님).
     *
     * this(=store 루트 인스턴스, Vuex가 action handler를 store에 bind해 호출)를 이용해
     * userHasMenuLink/workOrderApi가 기대하는 루트 store를 그대로 넘긴다.
     */
    async refresh(context) {
      const rootStore = this;

      if (!localStorage.getItem('accessTokenManu')) {
        context.commit('setResult', { count: 0, eligible: false, truncated: false });
        return;
      }

      context.commit('setLoading', true);
      try {
        const hasMenu = await userHasMenuLink(rootStore, '/lab-orders/manual');
        if (!hasMenu) {
          context.commit('setResult', { count: 0, eligible: false, truncated: false });
          return;
        }

        const api = workOrderApi(chinaInapi, rootStore);
        let page = 1;
        let totalPages = 1;
        let count = 0;
        let truncated = false;

        while (page <= MAX_PAGES) {
          const res = await api.getWorkOrders({ page, limit: PAGE_LIMIT });
          const body = res.data || {};
          const rows = Array.isArray(body.data) ? body.data : [];
          count += rows.filter(hasMissingRandomNo).length;
          totalPages = Number(body.totalPages) || 1;

          if (page >= totalPages) break;
          if (page === MAX_PAGES) {
            // 상한에 도달했는데 아직 남은 페이지가 있음 — 집계가 불완전함을 표시
            truncated = true;
            break;
          }
          page += 1;
        }

        context.commit('setResult', { count, eligible: true, truncated });
      } catch (e) {
        // ambient 안내용 집계 — 실패해도 이전 값을 유지하고 조용히 종료(에러 UI 없음)
        console.warn('[missingRandomNo] refresh 실패, 이전 값 유지:', e);
      } finally {
        context.commit('setLoading', false);
      }
    },
  },
};
